<!-- MOLECULE: AccountMenuCardLanguage.vue -->
<!-- Language selector dropdown in the account menu card. -->

<script setup lang="ts">
//
// Composables
//

// Router
const router = useRouter();

// Localization
const { locale, t } = useI18n();
const switchLocalePath = useSwitchLocalePath();

//
// Constants
//
const availableLanguages = ["en", "bg", "mk"] as const;

//
// State
//
const languageMenuOpen = ref(false);

//
// Methods
//
const getFlagForLanguage = (lang: string): string => ({ en: "us", bg: "bg", mk: "mk" })[lang] || "us";

const changeLanguage = (lang: "en" | "bg" | "mk") => {
  const newPath = switchLocalePath(lang);
  if (newPath) router.push(newPath);
};
</script>

<template>
  <div>
    <v-tooltip location="bottom" :open-delay="700" :close-delay="300">
      <template #activator="{ props: tooltipProps }">
        <v-hover>
          <template #default="{ isHovering, props: hoverProps }">
            <div
              v-bind="{ ...tooltipProps, ...hoverProps }"
              class="text-caption px-8 d-flex align-center ga-2 py-1 mb-2 cursor-pointer"
              :class="{ 'bg-secondary': isHovering }"
              @click="languageMenuOpen = !languageMenuOpen"
            >
              <v-icon icon="mdi-web" />
              <span>
                {{
                  t("components.common.app-bar.app_bar_actions.account_menu.account_menu_card.language_section.title")
                }}
              </span>

              <v-spacer />

              <v-icon :icon="languageMenuOpen ? 'mdi-chevron-down' : 'mdi-chevron-right'" size="small" />
            </div>
          </template>
        </v-hover>
      </template>

      <span>{{
        t("components.common.app-bar.app_bar_actions.account_menu.account_menu_card.language_section.tooltip")
      }}</span>
    </v-tooltip>

    <v-expand-transition>
      <v-list v-show="languageMenuOpen" nav density="compact" class="bg-primary text-white px-8 py-0">
        <v-list-item
          v-for="lang in availableLanguages"
          :key="lang"
          :value="lang"
          rounded="lg"
          :class="{ 'bg-secondary': locale === lang }"
          class="mb-1"
          @click="changeLanguage(lang)"
        >
          <v-list-item-title class="d-flex align-center ga-2">
            <CircleFlags
              :country="getFlagForLanguage(lang)"
              style="width: 16px; height: 16px; border-radius: 50%; overflow: clip"
            />
            <span class="text-caption" :class="{ 'font-weight-bold': locale === lang }">
              {{
                t(
                  `components.common.app-bar.app_bar_actions.account_menu.account_menu_card.language_section.languages.${lang}`,
                )
              }}
            </span>
          </v-list-item-title>
        </v-list-item>
      </v-list>
    </v-expand-transition>
  </div>
</template>

<style scoped>
.v-list-item--density-compact.v-list-item--one-line {
  min-height: 0 !important;
}
</style>
